import {
  clampDateTo,
  datePl,
  type FleetConflictDto,
  ODOMETER_SOURCES,
  type OdometerDto,
  type OdometerSource,
  todayInWarsaw,
  TYRE_NEEDS,
  TYRE_OPS,
  TYRE_SEASONS,
  TYRE_STATES,
  type TyreNeed,
  type TyreSeason,
  type TyreSetDto,
  type TyreSetInput,
  type TyreState,
  UNAVAILABILITY_REASONS,
  type UnavailabilityDto,
  type UnavailabilityReason,
  type VehicleDto,
} from '@bqcrm/shared';
import { Alert, Badge, Button, Card, Group, List, Modal, NumberInput, Select, SimpleGrid, Stack, Switch, Table, Text, Textarea, TextInput } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconPlus, IconX } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../../api';
import { ask } from '../../../confirm';
import { OdometerStateBadge } from '../../../components/Badges';
import { QueryState } from '../../../components/QueryState';
import { EmptyState, SectionCard } from '../../../components/ui';
import { dayRange, formatDateTime } from '../../../format';
import { notifyError, notifyOk, notifyWarn } from '../../../notify';
import { kmPl, useFleetInvalidate, useFleetVehicles, vehicleLabel } from '../common';

const num = (v: string | number) => (typeof v === 'number' ? v : null);

// ── opony ──

type TyreForm = Required<{ [K in keyof TyreSetInput]: Exclude<TyreSetInput[K], undefined> }>;

function TyreSetModal({ v, set, onClose }: { v: VehicleDto; set?: TyreSetDto; onClose: () => void }) {
  const invalidate = useFleetInvalidate();
  const [f, setF] = useState<TyreForm>(
    set
      ? { season: set.season, size: set.size, brand: set.brand, model: set.model, state: set.state, storage: set.storage, need: set.need, note: set.note }
      : { season: 'letnie', size: '', brand: '', model: '', state: 'przechowywane', storage: '', need: 'brak', note: '' },
  );
  const upd = (p: Partial<TyreForm>) => setF((x) => ({ ...x, ...p }));
  const save = useMutation({
    mutationFn: () => api(set ? `/fleet/vehicles/${v.id}/tyres/${set.id}` : `/fleet/vehicles/${v.id}/tyres`, { method: set ? 'PUT' : 'POST', body: f }),
    onSuccess: () => {
      notifyOk(set ? 'Zapisano komplet opon' : 'Dodano komplet opon');
      invalidate();
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title={set ? 'Edycja kompletu opon' : 'Nowy komplet opon'} size="lg">
      <Stack gap="sm">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <Select label="Rodzaj" data={Object.entries(TYRE_SEASONS).map(([value, label]) => ({ value, label }))} value={f.season} onChange={(x) => x && upd({ season: x as TyreSeason })} allowDeselect={false} />
          <TextInput label="Rozmiar" required placeholder="np. 215/65 R16C" value={f.size} onChange={(e) => upd({ size: e.currentTarget.value })} />
          <TextInput label="Producent" value={f.brand} onChange={(e) => upd({ brand: e.currentTarget.value })} />
          <TextInput label="Model" value={f.model} onChange={(e) => upd({ model: e.currentTarget.value })} />
          <Select label="Stan" data={Object.entries(TYRE_STATES).map(([value, label]) => ({ value, label }))} value={f.state} onChange={(x) => x && upd({ state: x as TyreState })} allowDeselect={false} />
          <Select label="Potrzeba" data={Object.entries(TYRE_NEEDS).map(([value, label]) => ({ value, label }))} value={f.need} onChange={(x) => x && upd({ need: x as TyreNeed })} allowDeselect={false} />
        </SimpleGrid>
        <TextInput label="Miejsce przechowywania" value={f.storage} onChange={(e) => upd({ storage: e.currentTarget.value })} />
        <Textarea label="Uwagi o stanie" autosize minRows={2} value={f.note} onChange={(e) => upd({ note: e.currentTarget.value })} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button disabled={f.size.trim().length < 3} loading={save.isPending} onClick={() => save.mutate()}>
            Zapisz
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

type TyreOpKind = 'zalozenie' | 'zdjecie' | 'przechowanie' | 'wycofanie';

function TyreOpModal({ set, op, onClose }: { set: TyreSetDto; op: TyreOpKind; onClose: () => void }) {
  const invalidate = useFleetInvalidate();
  const [date, setDate] = useState(todayInWarsaw());
  const [km, setKm] = useState<number | null>(null);
  const [storage, setStorage] = useState(set.storage);
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () => api(`/fleet/tyres/${set.id}/op`, { method: 'POST', body: { op, date, km, storage, note } }),
    onSuccess: () => {
      notifyOk('Zapisano');
      invalidate();
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title={`${TYRE_OPS[op]}: ${TYRE_SEASONS[set.season].toLowerCase()} ${set.size}`}>
      <Stack gap="sm">
        <TextInput label="Data" type="date" required value={date} onChange={(e) => setDate(e.currentTarget.value)} />
        <NumberInput label="Przebieg" suffix=" km" min={0} thousandSeparator=" " allowDecimal={false} value={km ?? ''} onChange={(x) => setKm(num(x))} />
        {(op === 'przechowanie' || op === 'zdjecie') && <TextInput label="Miejsce przechowywania" value={storage} onChange={(e) => setStorage(e.currentTarget.value)} />}
        <Textarea label="Uwagi" autosize minRows={2} value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button color={op === 'wycofanie' ? 'red' : undefined} disabled={!date} loading={save.isPending} onClick={() => save.mutate()}>
            Zapisz
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Opony: komplety, stan, przechowywanie, potrzeby i historia operacji. */
export function TyresTab({ v }: { v: VehicleDto }) {
  const q = useQuery({ queryKey: ['fleet', 'vehicle', v.id, 'tyres'], queryFn: () => api<TyreSetDto[]>(`/fleet/vehicles/${v.id}/tyres`) });
  const [editing, setEditing] = useState<TyreSetDto | 'new' | null>(null);
  const [op, setOp] = useState<{ set: TyreSetDto; op: TyreOpKind } | null>(null);
  const manage = v.can.manage;
  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Sezonowa wymiana to zwykła czynność „Wymiana opon” w zakładce „Terminy i serwisy”.
        </Text>
        {manage && (
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setEditing('new')}>
            Dodaj komplet opon
          </Button>
        )}
      </Group>
      <QueryState isLoading={q.isLoading} error={q.error}>
        {q.data?.length ? (
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            {q.data.map((s) => (
              <SectionCard
                key={s.id}
                title={`${TYRE_SEASONS[s.season]} · ${s.size}`}
                actions={
                  manage &&
                  s.state !== 'wycofane' && (
                    <Button size="xs" variant="subtle" onClick={() => setEditing(s)}>
                      Edytuj
                    </Button>
                  )
                }
              >
                <Stack gap={6}>
                  <Group gap={6}>
                    <Badge size="sm" variant="light" color={s.state === 'zalozone' ? 'green' : s.state === 'wycofane' ? 'gray' : 'blue'}>
                      {TYRE_STATES[s.state]}
                    </Badge>
                    {s.need !== 'brak' && (
                      <Badge size="sm" variant="light" color="orange">
                        potrzeba: {TYRE_NEEDS[s.need].toLowerCase()}
                      </Badge>
                    )}
                  </Group>
                  <Text size="sm">{[s.brand, s.model].filter(Boolean).join(' ') || 'producent nie podany'}</Text>
                  {s.storage && <Text size="sm">Przechowywanie: {s.storage}</Text>}
                  {s.note && (
                    <Text size="sm" c="dimmed">
                      {s.note}
                    </Text>
                  )}
                  {manage && s.state !== 'wycofane' && (
                    <Group gap={4}>
                      {s.state !== 'zalozone' && (
                        <Button size="compact-sm" variant="light" onClick={() => setOp({ set: s, op: 'zalozenie' })}>
                          Załóż
                        </Button>
                      )}
                      {s.state === 'zalozone' && (
                        <Button size="compact-sm" variant="light" onClick={() => setOp({ set: s, op: 'zdjecie' })}>
                          Zdejmij
                        </Button>
                      )}
                      <Button size="compact-sm" variant="subtle" onClick={() => setOp({ set: s, op: 'przechowanie' })}>
                        Przekaż do przechowania
                      </Button>
                      <Button size="compact-sm" variant="subtle" color="red" onClick={() => setOp({ set: s, op: 'wycofanie' })}>
                        Wycofaj
                      </Button>
                    </Group>
                  )}
                  {s.ops.length > 0 && (
                    <Table mt={4}>
                      <Table.Tbody>
                        {s.ops.map((o, i) => (
                          <Table.Tr key={i}>
                            <Table.Td>
                              <Text size="xs">{datePl(o.date)}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="xs">
                                {TYRE_OPS[o.op]}
                                {o.km !== null ? ` · ${kmPl(o.km)}` : ''}
                                {o.note ? ` · ${o.note}` : ''}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="xs" c="dimmed">
                                {o.byName ?? '—'}
                              </Text>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  )}
                </Stack>
              </SectionCard>
            ))}
          </SimpleGrid>
        ) : (
          <Card>
            <EmptyState compact title="Brak kompletów opon" hint="Dodaj komplety (letnie, zimowe, całoroczne) z rozmiarem i miejscem przechowywania." />
          </Card>
        )}
      </QueryState>
      {editing && <TyreSetModal v={v} set={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />}
      {op && <TyreOpModal set={op.set} op={op.op} onClose={() => setOp(null)} />}
    </Stack>
  );
}

// ── przebieg ──

function OdometerModal({ v, onClose }: { v: VehicleDto; onClose: () => void }) {
  const invalidate = useFleetInvalidate();
  const [km, setKm] = useState<number | null>(null);
  const [readAt, setReadAt] = useState(todayInWarsaw());
  const [source, setSource] = useState<OdometerSource>('reczny');
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () => api<OdometerDto[]>(`/fleet/vehicles/${v.id}/odometer`, { method: 'POST', body: { km, readAt, source, note } }),
    onSuccess: (rows) => {
      const added = rows.find((r) => r.km === km && r.state === 'do_weryfikacji');
      if (added) notifyWarn(added.issue || 'Odczyt wymaga weryfikacji', 'Odczyt zapisany do weryfikacji');
      else notifyOk('Zapisano odczyt przebiegu');
      invalidate();
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Nowy odczyt przebiegu">
      <Stack gap="sm">
        <NumberInput label="Przebieg" required suffix=" km" min={0} thousandSeparator=" " allowDecimal={false} value={km ?? ''} onChange={(x) => setKm(num(x))} data-autofocus />
        <TextInput label="Data odczytu" description="Kiedy licznik pokazywał ten stan (nie data wpisu)" type="date" required max={todayInWarsaw()} value={readAt} onChange={(e) => setReadAt(e.currentTarget.value)} />
        <Select label="Źródło" data={Object.entries(ODOMETER_SOURCES).map(([value, label]) => ({ value, label }))} value={source} onChange={(x) => x && setSource(x as OdometerSource)} allowDeselect={false} />
        <TextInput label="Uwagi" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button disabled={km === null || !readAt} loading={save.isPending} onClick={() => save.mutate()}>
            Zapisz odczyt
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Przebieg: odczyty z datą odczytu i datą zapisu, źródłem i weryfikacją. */
export function OdometerTab({ v }: { v: VehicleDto }) {
  const q = useQuery({ queryKey: ['fleet', 'vehicle', v.id, 'odometer'], queryFn: () => api<OdometerDto[]>(`/fleet/vehicles/${v.id}/odometer`) });
  const invalidate = useFleetInvalidate();
  const [adding, setAdding] = useState(false);
  const verify = useMutation({
    mutationFn: (b: { id: string; decision: 'ok' | 'odrzucony' }) => api(`/fleet/odometer/${b.id}/verify`, { method: 'POST', body: { decision: b.decision, note: '' } }),
    onSuccess: (_r, b) => {
      notifyOk(b.decision === 'ok' ? 'Potwierdzono odczyt' : 'Odrzucono odczyt');
      invalidate();
    },
    onError: notifyError,
  });
  return (
    <Stack gap="md">
      {v.odometer?.stale && (
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={18} />}>
          Ostatni potwierdzony odczyt ({kmPl(v.odometer.km)}, {datePl(v.odometer.readAt)}) jest nieaktualny — czynności kilometrowe mają stan „brak aktualnych danych”.
        </Alert>
      )}
      <SectionCard
        title="Odczyty przebiegu"
        padding={0}
        actions={
          v.can.manage && (
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAdding(true)}>
              Dodaj odczyt
            </Button>
          )
        }
      >
        <QueryState isLoading={q.isLoading} error={q.error}>
          {q.data?.length ? (
            <Table.ScrollContainer minWidth={820}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th ta="right">Przebieg</Table.Th>
                    <Table.Th>Data odczytu</Table.Th>
                    <Table.Th>Źródło</Table.Th>
                    <Table.Th>Stan</Table.Th>
                    <Table.Th>Zapisano</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {q.data.map((r) => (
                    <Table.Tr key={r.id} style={r.state === 'odrzucony' ? { opacity: 0.55 } : undefined}>
                      <Table.Td ta="right" className="tabular">
                        <Text size="sm" fw={600} td={r.state === 'odrzucony' ? 'line-through' : undefined}>
                          {kmPl(r.km)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{formatDateTime(r.readAt)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{ODOMETER_SOURCES[r.source]}</Text>
                        {r.note && (
                          <Text size="xs" c="dimmed">
                            {r.note}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <OdometerStateBadge state={r.state} />
                        {r.issue && (
                          <Text size="xs" c={r.state === 'do_weryfikacji' ? 'var(--app-text-orange)' : 'dimmed'}>
                            {r.issue}
                          </Text>
                        )}
                        {r.verifiedByName && (
                          <Text size="xs" c="dimmed">
                            weryfikacja: {r.verifiedByName}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">{formatDateTime(r.recordedAt)}</Text>
                        <Text size="xs" c="dimmed">
                          {r.recordedByName ?? '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        {v.can.manage && r.state === 'do_weryfikacji' && (
                          <Group gap={4} justify="flex-end" wrap="nowrap">
                            <Button size="compact-sm" variant="light" color="green" leftSection={<IconCheck size={14} />} onClick={() => verify.mutate({ id: r.id, decision: 'ok' })}>
                              Potwierdź
                            </Button>
                            <Button size="compact-sm" variant="subtle" color="red" leftSection={<IconX size={14} />} onClick={() => verify.mutate({ id: r.id, decision: 'odrzucony' })}>
                              Odrzuć
                            </Button>
                          </Group>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          ) : (
            <EmptyState compact title="Brak odczytów przebiegu" hint="Przebieg przychodzi z serwisu, zgłoszeń użytkownika, wykonania czynności albo wpisu ręcznego." />
          )}
        </QueryState>
      </SectionCard>
      <Text size="xs" c="dimmed">
        Odczyt niższy od wcześniejszego, wyższy od późniejszego albo z nierealnym przyrostem trafia do weryfikacji i nie wpływa na terminy, dopóki ktoś go nie potwierdzi.
      </Text>
      {adding && <OdometerModal v={v} onClose={() => setAdding(false)} />}
    </Stack>
  );
}

// ── dostępność ──

export function FleetConflictsAlert({ conflicts }: { conflicts: FleetConflictDto[] }) {
  if (!conflicts.length) return null;
  return (
    <Alert color="yellow" icon={<IconAlertTriangle size={18} />} title={`Kolizje z pracami ekipy (${conflicts.length})`}>
      <Text size="sm" mb={4}>
        Nie przesuwamy prac automatycznie — sprawdź plan i ewentualnie zapewnij auto zastępcze.
      </Text>
      <List size="sm" spacing={2}>
        {conflicts.slice(0, 12).map((c, i) => (
          <List.Item key={`${c.taskId}-${c.day}-${i}`}>{c.text}</List.Item>
        ))}
      </List>
    </Alert>
  );
}

function UnavailabilityModal({ v, onClose, onConflicts }: { v: VehicleDto; onClose: () => void; onConflicts: (c: FleetConflictDto[]) => void }) {
  const invalidate = useFleetInvalidate();
  const vehicles = useFleetVehicles();
  const [dateFrom, setDateFrom] = useState(todayInWarsaw());
  const [dateTo, setDateTo] = useState(todayInWarsaw());
  const [reason, setReason] = useState<UnavailabilityReason>('serwis');
  const [note, setNote] = useState('');
  const [replacementVehicleId, setReplacement] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api<{ items: UnavailabilityDto[]; conflicts: FleetConflictDto[] }>(`/fleet/vehicles/${v.id}/unavailability`, { method: 'POST', body: { dateFrom, dateTo, reason, note, replacementVehicleId } }),
    onSuccess: (r) => {
      if (r.conflicts.length) notifyWarn(`Pojazd jest przypisany do prac w tych dniach (${r.conflicts.length}). Prace nie zostały przesunięte.`, 'Zapisano niedostępność');
      else notifyOk('Zapisano niedostępność');
      onConflicts(r.conflicts);
      invalidate();
      onClose();
    },
    onError: notifyError,
  });
  const others = (vehicles.data ?? []).filter((x) => x.id !== v.id && x.status === 'aktywny' && x.companyId === v.companyId);
  return (
    <Modal opened onClose={onClose} title="Nowa niedostępność pojazdu">
      <Stack gap="sm">
        <Group grow>
          <TextInput label="Od" type="date" required value={dateFrom} onChange={(e) => {
            const f = e.currentTarget.value;
            setDateFrom(f);
            setDateTo((t) => clampDateTo(f, t));
          }} />
          <TextInput label="Do" type="date" required min={dateFrom || undefined} value={dateTo} onChange={(e) => setDateTo(e.currentTarget.value)} />
        </Group>
        <Select label="Przyczyna" data={Object.entries(UNAVAILABILITY_REASONS).map(([value, label]) => ({ value, label }))} value={reason} onChange={(x) => x && setReason(x as UnavailabilityReason)} allowDeselect={false} />
        <Textarea label="Opis" autosize minRows={2} value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        <Select label="Auto zastępcze (opcjonalnie)" clearable searchable data={others.map((x) => ({ value: x.id, label: vehicleLabel(x) }))} value={replacementVehicleId} onChange={setReplacement} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button disabled={!dateFrom || !dateTo} loading={save.isPending} onClick={() => save.mutate()}>
            Zapisz niedostępność
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Dostępność: okresy niedostępności (serwis, naprawa, szkoda) — informacja dla planowania, bez przesuwania prac. */
export function UnavailabilityTab({ v }: { v: VehicleDto }) {
  const q = useQuery({ queryKey: ['fleet', 'vehicle', v.id, 'unavailability'], queryFn: () => api<UnavailabilityDto[]>(`/fleet/vehicles/${v.id}/unavailability`) });
  const invalidate = useFleetInvalidate();
  const [adding, setAdding] = useState(false);
  const [conflicts, setConflicts] = useState<FleetConflictDto[]>([]);
  const [showCancelled, setShowCancelled] = useState(false);
  const cancel = useMutation({
    mutationFn: (id: string) => api(`/fleet/unavailability/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      notifyOk('Anulowano niedostępność');
      invalidate();
    },
    onError: notifyError,
  });
  const today = todayInWarsaw();
  const rows = (q.data ?? []).filter((u) => showCancelled || !u.cancelledAt);
  return (
    <Stack gap="md">
      <FleetConflictsAlert conflicts={conflicts} />
      <SectionCard
        title="Niedostępność"
        padding={0}
        actions={
          <Group gap="xs">
            {(q.data ?? []).some((u) => u.cancelledAt) && <Switch size="xs" label="Pokaż anulowane" checked={showCancelled} onChange={(e) => setShowCancelled(e.currentTarget.checked)} />}
            {v.can.manage && (
              <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAdding(true)}>
                Dodaj niedostępność
              </Button>
            )}
          </Group>
        }
      >
        <QueryState isLoading={q.isLoading} error={q.error}>
          {rows.length ? (
            <Table.ScrollContainer minWidth={700}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Okres</Table.Th>
                    <Table.Th>Przyczyna</Table.Th>
                    <Table.Th>Auto zastępcze</Table.Th>
                    <Table.Th>Dodał(a)</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((u) => (
                    <Table.Tr key={u.id} style={u.cancelledAt ? { opacity: 0.55 } : u.dateFrom <= today && u.dateTo >= today ? { backgroundColor: 'var(--mantine-color-red-light)' } : undefined}>
                      <Table.Td>
                        <Text size="sm" td={u.cancelledAt ? 'line-through' : undefined}>
                          {dayRange(u.dateFrom, u.dateTo)}
                        </Text>
                        {u.cancelledAt && (
                          <Text size="xs" c="dimmed">
                            anulowano {formatDateTime(u.cancelledAt)}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{UNAVAILABILITY_REASONS[u.reason]}</Text>
                        {u.note && (
                          <Text size="xs" c="dimmed">
                            {u.note}
                          </Text>
                        )}
                        {u.visitId && (
                          <Text size="xs" c="dimmed">
                            z wizyty w serwisie
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{u.replacement?.plate ?? '—'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{u.createdByName ?? '—'}</Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        {v.can.manage && !u.cancelledAt && u.dateTo >= today && (
                          <Button
                            size="compact-sm"
                            variant="subtle"
                            color="red"
                            onClick={async () => (await ask('Pojazd znów będzie dostępny w tych dniach.', { title: 'Anulować niedostępność?', confirm: 'Anuluj niedostępność', danger: true })) && cancel.mutate(u.id)}
                          >
                            Anuluj
                          </Button>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          ) : (
            <EmptyState compact title="Pojazd nie ma zaplanowanej niedostępności" hint="Niedostępność widać w Kalendarzu i przy planowaniu prac ekipy — jako ostrzeżenie, bez blokady." />
          )}
        </QueryState>
      </SectionCard>
      {adding && <UnavailabilityModal v={v} onClose={() => setAdding(false)} onConflicts={setConflicts} />}
    </Stack>
  );
}
